import { Check, Clock } from "lucide-react";

/**
 * Timeline compacta de tempos do pedido (Recebido → Produção → Pronto → ... →
 * Entregue/Retirado). Esconde quando é Mesa (não faz sentido pra sessão de mesa)
 * ou quando nenhum timestamp foi gravado ainda.
 */
export function PedidoTimeline({ pedido }) {
  const isMesa = pedido.modulo === "Mesas" || pedido.mesa_id;
  if (isMesa) return null;
  const isPickup = pedido.tipoEntrega === "pickup";

  const steps = isPickup
    ? [
        { label: "Recebido", ts: pedido.created_at },
        { label: "Produção", ts: pedido.ts_producao },
        { label: "Pronto",   ts: pedido.ts_pronto },
        { label: "Retirado", ts: pedido.ts_concluido },
      ]
    : [
        { label: "Recebido", ts: pedido.created_at },
        { label: "Produção", ts: pedido.ts_producao },
        { label: "Pronto",   ts: pedido.ts_pronto },
        // Estado legado: só aparece se gravou ts_na_bag (fluxo descontinuado).
        ...(pedido.ts_na_bag ? [{ label: "Na Bag", ts: pedido.ts_na_bag }] : []),
        { label: "Em Rota",  ts: pedido.ts_em_rota },
        { label: "Entregue", ts: pedido.ts_concluido },
      ];

  const hasAnyTs = steps.some((s) => s.ts);
  if (!hasAnyTs) return null;

  let totalMs = 0;
  const stepsWithElapsed = steps.map((s, i) => {
    let diffMs = 0;
    if (s.ts) {
      const nextTs =
        i < steps.length - 1 && steps[i + 1]?.ts
          ? new Date(steps[i + 1].ts).getTime()
          : null;
      if (nextTs) diffMs = nextTs - new Date(s.ts).getTime();
      if (diffMs > 0) totalMs += diffMs;
    }
    return { ...s, diffMs };
  });

  return (
    <div className="bg-muted/30 rounded-lg p-2.5">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
        {stepsWithElapsed.map((s, i) => (
          <span
            key={i}
            className={`flex items-center gap-0.5 ${s.ts ? "text-foreground font-medium" : "text-muted-foreground"}`}
          >
            {s.ts ? (
              <Check className="w-3 h-3 text-primary" />
            ) : (
              <span className="w-3 h-3 rounded-full border border-muted-foreground/30 inline-block" />
            )}
            {s.label}
            {s.diffMs > 0 && (
              <span className="font-normal ml-0.5 text-orange-500">{fmtElapsed(s.diffMs)}</span>
            )}
          </span>
        ))}
      </div>
      {totalMs > 0 && (
        <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-muted text-[10px] text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span>
            Tempo total: <span className="font-semibold text-foreground">{fmtElapsed(totalMs)}</span>
          </span>
        </div>
      )}
    </div>
  );
}

function fmtElapsed(ms) {
  if (!ms || ms < 0) return "";
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return sec > 0 ? `${min}min ${sec}s` : `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}
